import React from 'react'

import { observer } from 'mobx-react'
import { Layer, MapRef, Source } from 'react-map-gl/mapbox'

import {
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '~/client/src/shared/map/mapZoomRule'

import BasemapRenderGuard from '../../../BasemapRenderGuard/BasemapRenderGuard'
import { isPlanDrawable } from '../../../MapBoxViewer/globeMapFiles'
import {
  PLAN_LAYER_PREFIX,
  PLAN_SOURCE,
} from '../../../MapBoxViewer/mapboxConstants'
import type { ISitemapWithBasemap } from '../../../MapBoxViewer/MapBoxViewer.store'

interface IProps {
  idx: number
  sitemapWithBasemap: ISitemapWithBasemap
  mapRef: MapRef
  isHidden?: boolean
}

@observer
export default class GlobeViewPlan extends React.Component<IProps> {
  public componentWillUnmount(): void {
    const { mapRef } = this.props
    try {
      if (mapRef?.getMap) {
        const map = mapRef.getMap()
        if (map) {
          // Remove layer first
          if (map.getLayer && map.getLayer(this.layerId)) {
            map.removeLayer(this.layerId)
          }
          if (map.getLayer && map.getLayer(this.layerId + '-clip')) {
            map.removeLayer(this.layerId + '-clip')
          }
          // Then remove source
          if (map.getSource && map.getSource(this.sourceId)) {
            map.removeSource(this.sourceId)
          }
        }
      }
    } catch {
      console.warn(`Error during map source ${this.sourceId} unmount.`)
    }
  }

  public render(): JSX.Element {
    const { sitemapWithBasemap } = this.props
    if (!isPlanDrawable(sitemapWithBasemap)) {
      return null
    }
    const { basemap } = sitemapWithBasemap
    return (
      <BasemapRenderGuard url={basemap.source}>
        {safeUrl => (
          <Source
            id={this.sourceId}
            type="image"
            key={this.sourceId}
            url={safeUrl}
            coordinates={this.coordinates}
          >
            <Layer
              id={this.layerId}
              source={this.sourceId}
              type="raster"
              maxzoom={MAX_ITEM_ZOOM_LEVEL}
              minzoom={PROJECT_MARKER_ZOOM_LEVEL}
              layout={{
                visibility: this.props.isHidden ? 'none' : 'visible',
              }}
              paint={{
                'raster-fade-duration': 0,
              }}
            />
          </Source>
        )}
      </BasemapRenderGuard>
    )
  }

  private get layerId(): string {
    return PLAN_LAYER_PREFIX + this.props.sitemapWithBasemap.sitemap.id
  }

  private get sourceId(): string {
    return PLAN_SOURCE + this.props.sitemapWithBasemap.sitemap.id
  }

  private get coordinates(): [
    [number, number],
    [number, number],
    [number, number],
    [number, number],
  ] {
    const { sitemap } = this.props.sitemapWithBasemap
    const geoCorners = sitemap.geoCorners
    return [
      [geoCorners[0].longitude, geoCorners[0].latitude], // top-left
      [geoCorners[1].longitude, geoCorners[1].latitude], // top-right
      [geoCorners[2].longitude, geoCorners[2].latitude], // bottom-right
      [geoCorners[3].longitude, geoCorners[3].latitude], // bottom-left
    ]
  }
}
